import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ToggleGroup, ToggleItem } from 'src/core/ui/ToggleGroup';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { FormatOption } from 'src/modals/EditTableColumns';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { applyNumberFormatting } from 'src/utils/numberFormatter';

import { InventoryEventTabs } from '../../InventoryEventTabs';
import { TabWithStarIcon } from '../../TabWithStarIcon';
import { useInventoryEventLocalContext } from '../InventoryEventLocalContext';
import * as styles from './InventoryEventHeader.css';

enum EventInventoryContentType {
  MyListings = 'MyListings',
  AllListings = 'AllListings',
}

export const InventoryEventHeaderNav = () => {
  const {
    defaultTab,
    activeTab,
    setActiveTab,
    onUpdateDefaultType,
    listings,
    compListings,
  } = useInventoryEventLocalContext();

  const isMobile = useMatchMedia('mobile');

  const [inventoryContentType, setInventoryContentType] =
    useState<EventInventoryContentType>();

  useEffect(() => {
    if (
      activeTab === InventoryEventTabs.ManualPricing ||
      activeTab === InventoryEventTabs.Info
    ) {
      setInventoryContentType(
        activeTab === InventoryEventTabs.ManualPricing
          ? EventInventoryContentType.AllListings
          : EventInventoryContentType.MyListings
      );
    }
  }, [activeTab]);

  const [listingOptionContent, listingOptionOverride] = useMemo(() => {
    const listingsCount = listings.map(flattenListingGroup).length;
    const compListingsCount = compListings.length;
    const listingOptionContent = {
      [EventInventoryContentType.MyListings]: ContentId.MyListings,
      [EventInventoryContentType.AllListings]: ContentId.AllListings,
    };

    const listingOptionOverride = {
      [EventInventoryContentType.MyListings]: (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="spaceBetween"
          style={{ width: '180px' }}
        >
          <TabWithStarIcon
            onIconClick={onUpdateDefaultType}
            defaultTab={defaultTab}
            tabType={InventoryEventTabs.Info}
            className={styles.startedInventoryContentType}
            persist
            avoidPointerEvents="star"
          >
            <Content id={ContentId.MyListings} />
          </TabWithStarIcon>
          <div>
            {applyNumberFormatting({
              inputNumber: listingsCount,
              precision: FormatOption.Whole,
            })}
          </div>
        </Stack>
      ),
      [EventInventoryContentType.AllListings]: (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="spaceBetween"
          style={{ width: '180px' }}
        >
          <TabWithStarIcon
            onIconClick={onUpdateDefaultType}
            defaultTab={defaultTab}
            tabType={InventoryEventTabs.ManualPricing}
            className={styles.startedInventoryContentType}
            persist
            avoidPointerEvents="star"
          >
            <Content id={ContentId.AllListings} />
          </TabWithStarIcon>
          <div>
            {applyNumberFormatting({
              inputNumber: listingsCount + compListingsCount,
              precision: FormatOption.Whole,
            })}
          </div>
        </Stack>
      ),
    };

    return [listingOptionContent, listingOptionOverride];
  }, [compListings.length, defaultTab, listings, onUpdateDefaultType]);

  const inventoryDisplay = useCallback(
    (value: string) => {
      const sanitzedContentType =
        value === EventInventoryContentType.MyListings
          ? InventoryEventTabs.Info
          : value === EventInventoryContentType.AllListings
            ? InventoryEventTabs.ManualPricing
            : undefined;
      if (!sanitzedContentType) {
        return <Content id={ContentId.NA} />;
      }
      return (
        <TabWithStarIcon
          onIconClick={onUpdateDefaultType}
          defaultTab={defaultTab}
          tabType={sanitzedContentType}
          className={styles.startedInventoryContentType}
          persist
          avoidPointerEvents="root"
        >
          <span onClick={() => setActiveTab(sanitzedContentType)}>
            <Content
              id={listingOptionContent[value as EventInventoryContentType]}
            />
          </span>
        </TabWithStarIcon>
      );
    },
    [defaultTab, listingOptionContent, onUpdateDefaultType, setActiveTab]
  );

  const isShowingInventory = useMemo(
    () =>
      activeTab === InventoryEventTabs.Info ||
      activeTab === InventoryEventTabs.ManualPricing,
    [activeTab]
  );

  const listingToggleOptions = useMemo((): ToggleItem[] => {
    return [
      {
        value: InventoryEventTabs.Info,
        children: (
          <TabWithStarIcon
            onIconClick={onUpdateDefaultType}
            defaultTab={defaultTab}
            tabType={InventoryEventTabs.Info}
            className={clsx(
              styles.startedInventoryContentType,
              styles.startedInventoryCnotentTypeWithContent
            )}
            persist
            avoidPointerEvents="star"
          >
            <div style={{ whiteSpace: 'nowrap' }}>
              <Content id={ContentId.MyListings} />
            </div>
          </TabWithStarIcon>
        ),
      },
      {
        value: InventoryEventTabs.ManualPricing,
        children: (
          <TabWithStarIcon
            onIconClick={onUpdateDefaultType}
            defaultTab={defaultTab}
            tabType={InventoryEventTabs.ManualPricing}
            className={clsx(
              styles.startedInventoryContentType,
              styles.startedInventoryCnotentTypeWithContent
            )}
            persist
            avoidPointerEvents="star"
          >
            <div style={{ whiteSpace: 'nowrap' }}>
              <Content id={ContentId.AllListings} />
            </div>
          </TabWithStarIcon>
        ),
      },
    ];
  }, [defaultTab, onUpdateDefaultType]);

  if (!isMobile) {
    return (
      <ToggleGroup
        value={activeTab}
        itemClassName={styles.toggleGroupItemParent}
        itemSelectedClassName={styles.toggleGroupItemSelected}
        onValueChange={(value) => {
          if (value) {
            setActiveTab(value as InventoryEventTabs);
          }
        }}
        options={listingToggleOptions}
      />
    );
  }

  return (
    <PosEnumSelect
      className={clsx(styles.inventoryContentDropdown, {
        [styles.inventoryContentDropdownActive]: isShowingInventory,
      })}
      textColorOverride={
        isShowingInventory ? vars.color.textInverted : vars.color.textStrong
      }
      displayComponent={inventoryDisplay}
      value={inventoryContentType}
      valueOptionsContent={listingOptionContent}
      valueOptionsContentOverride={listingOptionOverride}
      placeholderText={ContentId.None}
      onClick={(e) => e.stopPropagation()}
      onChange={(value) => {
        if (value) {
          const santized = value as unknown as EventInventoryContentType;
          setActiveTab(
            santized === EventInventoryContentType.MyListings
              ? InventoryEventTabs.Info
              : InventoryEventTabs.ManualPricing
          );
        }
      }}
    />
  );
};
